/**
 * VFS mount proxy — the request half of the service worker's `webdav-mount`
 * plugin: a VFS request goes out to the mount's WebDAV server, and the
 * server's answer comes back as a Response. `swMounts.js` is the routing half.
 *
 * Extracted from sw.js on 2026-09-24 so it could be tested. Four defects had
 * lived inline, where no test reached them:
 *
 *   1. PUT went through `putFileContents` with the page's `Headers` object.
 *      The webdav client merges headers with `Object.keys()`, which sees no
 *      entries on a `Headers`, so every page header — `If-Match`,
 *      `Content-Type` — was dropped. And it answered `new Response(true)`:
 *      200 with body "true", whatever the upstream said.
 *   2. Every other method passed its body as `body:`. The client reads `data`,
 *      so PROPPATCH, LOCK and PROPFIND bodies were silently dropped.
 *   3. An upstream error other than 401 was thrown out of the plugin. The
 *      registry logged it and moved on, and the request fell through to the
 *      page's own origin, which answered for a server it isn't (usually 404
 *      or 405). A refused DELETE, a 412, a 507 over quota: all invisible.
 *   4. A failed GET — a plain 404 included — became a 500 "offline".
 *
 * The rule now: **an upstream HTTP answer is passed through as-is** — status,
 * headers, body. Only a request that got no HTTP answer at all (network,
 * CORS, a wedged peer) becomes the caller's failure response. A 401 is still
 * thrown, because the caller heals a stale leaf token and retries.
 */

/** Statuses whose Response must be constructed with a null body. */
const NULL_BODY = new Set([101, 103, 204, 205, 304])

/**
 * Request headers never forwarded upstream: the connection's own, which the
 * outgoing fetch sets for itself.
 */
const DROP_REQUEST_HEADERS = new Set(['host', 'content-length', 'connection'])

/**
 * A plain object of forwardable request headers, lower-cased. Accepts a
 * `Headers` or a plain object. Plain because the webdav client merges with
 * `Object.keys()` — see defect 1 above.
 */
export function forwardableHeaders(headers) {
    const entries = headers && typeof headers.entries === 'function' ? [...headers.entries()] : Object.entries(headers || {})
    const out = {}
    for (const [k, v] of entries) {
        const key = String(k).toLowerCase()
        if (!DROP_REQUEST_HEADERS.has(key)) out[key] = v
    }
    return out
}

/**
 * Rebuild an upstream Response for the page. Text bodies have the server's
 * path prefix rewritten to the VFS prefix, so hrefs in a multistatus (or an
 * error body) name VFS paths; a null-body status gets a null body.
 */
async function reframe(resp, serverPrefix, vfsPrefix) {
    const init = { status: resp.status, statusText: resp.statusText, headers: resp.headers }
    if (NULL_BODY.has(resp.status)) return new Response(null, init)
    let text = await resp.text()
    if (serverPrefix && vfsPrefix) text = rewriteHrefs(text, serverPrefix, vfsPrefix)
    return new Response(text, init)
}

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** What a path inside the mount is appended to: the mountpoint, or nothing for a root mount. */
const hrefBase = (vfsPrefix) => (vfsPrefix === '/' ? '' : vfsPrefix)

/**
 * Upstream hrefs → VFS hrefs, in both forms a server writes them: the full
 * URL (Nephele, `https://host/sub/x`) and the absolute PATH (BrowserDAV
 * behind the group proxy, `/groups/browserdav/<srv>/<share>/x`). Found
 * 2026-09-28: the path form passed through un-rewritten, so a sync over a
 * BrowserDAV mount saw hrefs outside the mount's namespace and (rightly)
 * refused to compare. The path form is matched only as an href value — right
 * after `>` or a quote — and never for a mount of a server's root, where the
 * bare `/` would match everything.
 */
export function rewriteHrefs(text, serverPrefix, vfsPrefix) {
    // Before a `/` the prefix gives way to the mountpoint's base, which for a
    // root mount is empty: `/` + `/dir/` would be `//dir/`, another host
    const base = hrefBase(vfsPrefix)
    let out = text.replace(new RegExp(`${escapeRe(serverPrefix)}(?=/)`, 'g'), base).replaceAll(serverPrefix, vfsPrefix)
    let serverPath = null
    try {
        serverPath = new URL(serverPrefix).pathname.replace(/\/+$/, '')
    } catch {
        serverPath = null
    }
    if (serverPath) {
        out = out
            .replace(new RegExp(`([>"'])${escapeRe(serverPath)}(?=/)`, 'g'), `$1${base}`)
            .replace(new RegExp(`([>"'])${escapeRe(serverPath)}(?=[<"']|$)`, 'g'), `$1${vfsPrefix}`)
    }
    return out
}

/**
 * The HTTP status an error from the webdav client carries, or null when the
 * request never got an HTTP answer. The client's `createErrorFromResponse`
 * sets both `status` and `response`.
 */
export function upstreamStatus(err) {
    return err?.status ?? err?.response?.status ?? null
}

/**
 * Proxy one VFS request through a webdav client.
 *
 * @param {object} o
 * @param {string} o.method
 * @param {Headers|object} o.headers           the page's request headers
 * @param {ArrayBuffer|null} o.body            already read (a retry resends it)
 * @param {string} o.relativePath              upstream path (resolveMountPath)
 * @param {object} o.davClient                 webdav client: customRequest(path, opts)
 * @param {string} o.subPath                   the mount's subPath ('' for none)
 * @param {string} o.vfsPrefix                 the mountpoint
 * @param {string} o.serverPrefix              serverUrl + subPath
 * @param {(dest: string) => string|null} o.rewriteDestination  VFS → upstream, null if outside the mount
 * @param {(err: unknown) => Response} o.onNetworkError           no HTTP answer at all
 * @returns {Promise<Response>}  never undefined; throws only for a 401
 */
export async function proxyMountRequest({
    method,
    headers,
    body,
    relativePath,
    davClient,
    subPath = '',
    vfsPrefix,
    serverPrefix,
    rewriteDestination,
    onNetworkError,
}) {
    try {
        if (method === 'GET') {
            // One request carrying X-Acequia-Directory-JSON: the server's
            // plugin answers JSON for a directory (echoing the header), or
            // serves the file.
            const resp = await davClient.customRequest(relativePath, {
                method: 'GET',
                headers: { 'X-Acequia-Directory-JSON': 'true' },
            })
            const isDirJson = resp.headers?.get?.('x-acequia-directory-json') || resp.headers?.['x-acequia-directory-json']
            if (isDirJson) {
                // Directory listing: rewrite its paths into VFS terms, and echo
                // the marker so callers (the htmx layer) can tell a listing
                // from a .json file.
                const text = (await resp.text()).replaceAll(`"${subPath || ''}/`, `"${hrefBase(vfsPrefix)}/`)
                return new Response(text, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Acequia-Directory-JSON': 'true',
                        'Cache-Control': 'no-store',
                    },
                })
            }
            // A file: pass it through untouched — bytes are not rewritten.
            return new Response(resp.body, { status: resp.status, statusText: resp.statusText, headers: resp.headers })
        }

        if (method === 'MKCOL') {
            const resp = await davClient.customRequest(relativePath, { method: 'MKCOL' })
            return new Response(null, { status: resp.status, statusText: resp.statusText, headers: resp.headers })
        }

        // PUT and everything else: forward the page's headers (conditionals
        // included) and its body, as `data` — the key the client reads.
        const fwd = forwardableHeaders(headers)
        if (fwd.destination) {
            // MOVE/COPY name their target in VFS terms; the upstream needs its
            // own URL. A destination outside this mount is refused —
            // cross-mount moves are not one WebDAV operation.
            const dest = rewriteDestination(fwd.destination)
            if (!dest) return new Response('Destination outside this mount', { status: 502 })
            fwd.destination = dest
        }
        const resp = await davClient.customRequest(relativePath, {
            method,
            headers: fwd,
            // The client throws on `data: null`; undefined means no body.
            ...(body != null ? { data: body } : {}),
        })
        return reframe(resp, serverPrefix, vfsPrefix)
    } catch (err) {
        const status = upstreamStatus(err)
        if (status === 401) throw err // the caller heals a stale leaf and retries
        if (status) {
            // The upstream answered. Say what it said — never fall through to
            // the page's own origin, never turn it into "offline".
            return err.response ? reframe(err.response, serverPrefix, vfsPrefix) : new Response(null, { status })
        }
        return onNetworkError(err)
    }
}
